import RiotAssetNotice from './RiotAssetNotice';
import React,{useEffect,useRef,useState} from 'react';
import {useLocation,useNavigate,useParams,useSearchParams} from 'react-router-dom';
import PlaybookLibrary from './PlaybookLibrary';
import {useAuth} from '../../context/AuthContext';
import {useTeam} from '../../context/TeamContext';
import {useTeamRecord} from '../../context/useTeamRecord';
import {newId,newProject,decodeProject,clone,canEditTeam,validateProject} from '../../shared/stratsPlannerModel';
import {usePlannerRecords,plannerCall,editorId} from './usePlannerStore';
import StratsEditor from './StratsEditor';
import catalog from './catalog.json';
import './StratsPlanner.css';
import {PlannerDialogProvider,usePlannerDialogs} from './PlannerDialogs';
export default function StratsPlannerPage(){return <PlannerDialogProvider><StratsPlannerContent/></PlannerDialogProvider>;}
function StratsPlannerContent(){
 const dialogs=usePlannerDialogs();
 const location=useLocation(),pageAlive=useRef(true),route=useRef(location.pathname);route.current=location.pathname;
 useEffect(()=>{pageAlive.current=true;return()=>{pageAlive.current=false;};},[]);
 const {userData}=useAuth(),{teamId:activeTeamId}=useTeam(),{strategyId}=useParams(),[params]=useSearchParams(),navigate=useNavigate();
 const scope=strategyId&&params.get('scope')==='team'?'team':'personal',teamId=scope==='team'?(params.get('teamId')||activeTeamId):null;
 const safeId=value=>typeof value==='string'&&/^[A-Za-z0-9_-]{1,128}$/.test(value);
 const team=useTeamRecord(userData?.uid,safeId(strategyId?teamId:activeTeamId)?(strategyId?teamId:activeTeamId):null),enabled=!!userData?.uid&&(scope!=='team'||team.isActiveTeamMember)&&(!strategyId||safeId(strategyId));
 const records=usePlannerRecords(userData?.uid,scope,teamId,strategyId,enabled&&!!strategyId),canEdit=scope==='personal'||canEditTeam(team.team,userData?.uid);
 const personal=usePlannerRecords(userData?.uid,'personal',null,null,!!userData?.uid&&!strategyId);
 const teamRecords=usePlannerRecords(userData?.uid,'team',activeTeamId,null,!strategyId&&safeId(activeTeamId)&&team.isActiveTeamMember);
 const [busy,setBusy]=useState(false),[error,setError]=useState(''),[changingMap,setChangingMap]=useState(false);
 const operation=useRef(false);
 function open(id,destination={scope,teamId}){
  const query=destination.scope==='team'?'?'+new URLSearchParams({scope:'team',teamId:destination.teamId}):'';
  navigate('/strats-planner/'+id+query);
 }
 async function create(project,destination){
  if(operation.current)return;
  operation.current=true;
  setBusy(true);
  setError('');
  const base={scope:destination.scope,teamId:destination.teamId,editorId};
  try {
   validateProject(project,catalog);
   const result=await plannerCall({...base,action:'create',project});
   await plannerCall({...base,strategyId:result.data.id,action:'release'}).catch(()=>{});
   if(pageAlive.current&&route.current===location.pathname)open(result.data.id,destination);
  } catch(e){setError(e.message);}
  finally{operation.current=false;setBusy(false);}
 }
 async function rename(record,value){
  const title=value.trim();
  if(!title||title===record.title)return;
  await changeSavedStrategy(record,'save',()=>({...decodeProject(record.documentJson,catalog),title}));
 }
 async function remove(record){
  if(!record.canEdit||operation.current)return;
  const confirmed=await dialogs.confirm({
   title:'Delete strategy?',
   message:'This permanently deletes “'+record.title+'” and all its steps.',
   confirmLabel:'Delete strategy',
   danger:true
  });
  if(confirmed&&pageAlive.current)await changeSavedStrategy(record,'delete');
 }
 async function changeSavedStrategy(record,action,makeProject){
  if(!record.canEdit||operation.current)return;
  operation.current=true;
  setBusy(true);
  setError('');
  const base={scope:record.scope,teamId:record.teamId,strategyId:record.id,editorId:newId()};
  let acquired=false;
  try {
   const result=await plannerCall({...base,action:'acquire'});
   acquired=true;
   if(result.data.revision!==record.revision)throw Error('This strategy has changed. Wait for the library to refresh and try again.');
   const request={...base,action,revision:record.revision};
   if(makeProject)request.project=makeProject();
   await plannerCall(request);
   if(action==='delete')acquired=false;
  } catch(e){setError(e.message);}
  finally{
   if(acquired)await plannerCall({...base,action:'release'}).catch(()=>{});
   operation.current=false;
   setBusy(false);
  }
 }
 function duplicate(record){
  try {
   const project=clone(decodeProject(record.documentJson,catalog));
   create({...project,title:project.title.slice(0,115)+' copy'},record);
  } catch(e){setError(e.message);}
 }
 let content;
 if(scope==='team'&&team.loading)content=<p role="status">Checking team access…</p>;
 else if(scope==='team'&&!team.isActiveTeamMember)content=<div className="SP__empty"><h2>Choose an active team</h2><p>{team.error?.message||'You need current team membership to open this playbook. Select your team in Valovate, or return to My Playbooks.'}</p><button onClick={()=>navigate('/strats-planner')}>My Playbooks</button></div>;
 else if(strategyId&&!safeId(strategyId))content=<p role="alert">Invalid strategy link.</p>;
 else if(records.loading)content=<p role="status">Loading strategies…</p>;
 else if(records.error)content=<div role="alert" className="SP__notice">{records.error}<p>If this feature was just installed, its Firestore read rules and manageStratsPlanner function must be deployed.</p></div>;
 else if(strategyId){if(!records.data&&changingMap)content=<p role="status">Opening your new strategy…</p>;else if(!records.data)content=<div className="SP__empty"><h2>Strategy unavailable</h2><p>It may have been deleted, or this link belongs to a different account or team.</p><button onClick={()=>navigate('/strats-planner')}>Back to library</button></div>;else{try{decodeProject(records.data.documentJson,catalog);content=<StratsEditor key={JSON.stringify([userData.uid,scope,teamId,strategyId])} uid={userData.uid} {...{scope,teamId,canEdit}} teamName={team.team?.teamName||team.team?.name||teamId} record={records.data} onCreated={id=>{if(pageAlive.current&&route.current===location.pathname)open(id);}} onMapTransition={value=>{if(pageAlive.current)setChangingMap(value);}} onBack={()=>navigate('/strats-planner')}/>;}catch(e){content=<p role="alert">This strategy cannot be opened: {e.message}</p>;}}}
 else content=<PlaybookLibrary personal={personal} team={teamRecords} teamId={activeTeamId}
  canEditTeam={team.isActiveTeamMember&&canEditTeam(team.team,userData?.uid)} teamLoading={!!activeTeamId&&team.loading}
  busy={busy} onCreate={(mapId,destination)=>create(newProject(mapId),destination)} onRename={rename} onDuplicate={duplicate} onDelete={remove}/>;
 return <main className="SP">{error&&<p role="alert" className="SP__notice">{error}</p>}{busy&&<p role="status">Saving strategy…</p>}{content}<RiotAssetNotice/></main>;
}
